import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Header.css';
// import logo from '../assets/logo2.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faBars, faUser, faBell } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import NotificationList from './NotificationList';

function Header({ onLogoClick, onOpenMobileFilterBar }) {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLangTooltip, setShowLangTooltip] = useState(false);
    const menuRef = useRef();
    const langPopupRef = useRef();
    const { currentUser } = useAuth();
    const [profileImg, setProfileImg] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [guestFeedbackCount, setGuestFeedbackCount] = useState(0);
    const [hostFeedbackCount, setHostFeedbackCount] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);

    const prevPendingCount = useRef(pendingCount);
    const prevGuestFeedbackCount = useRef(guestFeedbackCount);
    const prevHostFeedbackCount = useRef(hostFeedbackCount);

    useEffect(() => {
        if (!currentUser) return;
        const userRef = doc(db, 'users', currentUser.uid);
        // מאזין בזמן אמת לשינויים בפרופיל
        const unsubscribe = onSnapshot(userRef, (userSnap) => {
            if (userSnap.exists()) {
                setProfileImg(userSnap.data().profileImg || null);
            }
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;
        // שלוף את כל הדינרים של המשתמש
        const dinnersRef = collection(db, 'dinners');
        const q = query(dinnersRef, where('hostId', '==', currentUser.uid));
        let unsubscribe2 = null;
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const dinnerIds = snapshot.docs.map(doc => doc.id);
            if (unsubscribe2) unsubscribe2();
            if (dinnerIds.length === 0) {
                setPendingCount(0);
                return;
            }
            // שלוף את כל הבקשות ל-dinners שלי בסטטוס pending
            const requestsRef = collection(db, 'dinnerRequests');
            const q2 = query(requestsRef, where('dinnerId', 'in', dinnerIds), where('status', '==', 'pending'));
            unsubscribe2 = onSnapshot(q2, (snapshot2) => {
                setPendingCount(snapshot2.size);
            });
        });
        return () => {
            unsubscribe();
            if (unsubscribe2) unsubscribe2();
        };
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;

        const checkPastDinnersAndFilterUnreviewed = async (snapshot, isGuestPerspective) => {
            let count = 0;
            for (const docSnap of snapshot.docs) {
                const data = docSnap.data();

                // Filter for unreviewed first
                const needsReview = isGuestPerspective ? !data.guestReview : !data.hostReview;
                if (!needsReview) continue;

                // Now check if it's in the past
                const dinnerId = data.dinnerId;
                if (!dinnerId) continue;
                const dinnerRef = doc(db, 'dinners', dinnerId);
                const dinnerSnap = await getDoc(dinnerRef);
                if (dinnerSnap.exists()) {
                    const dinnerDate = dinnerSnap.data().dinnerDetails?.availableDates?.[0];
                    if (dinnerDate && new Date(dinnerDate) < new Date()) {
                        count++;
                    }
                }
            }
            return count;
        };
        
        // Listener for guest's perspective
        const guestQuery = query(collection(db, 'dinnerRequests'), where('userId', '==', currentUser.uid), where('status', '==', 'accepted'));
        
        // Listener for host's perspective
        const hostDinnersQuery = query(collection(db, 'dinners'), where('hostId', '==', currentUser.uid));

        let unsubscribeHostRequests = () => {};

        const unsubscribeGuest = onSnapshot(guestQuery, async (snapshot) => {
            const count = await checkPastDinnersAndFilterUnreviewed(snapshot, true);
            setGuestFeedbackCount(count);
        });

        const unsubscribeHost = onSnapshot(hostDinnersQuery, (dinnersSnapshot) => {
            unsubscribeHostRequests(); // Clear previous listener
            const dinnerIds = dinnersSnapshot.docs.map(d => d.id);
            if (dinnerIds.length > 0) {
                const hostRequestsQuery = query(collection(db, 'dinnerRequests'), where('dinnerId', 'in', dinnerIds), where('status', '==', 'accepted'));
                unsubscribeHostRequests = onSnapshot(hostRequestsQuery, async (requestsSnapshot) => {
                    const count = await checkPastDinnersAndFilterUnreviewed(requestsSnapshot, false);
                    setHostFeedbackCount(count);
                });
            } else {
                setHostFeedbackCount(0);
            }
        });

        return () => {
            unsubscribeGuest();
            unsubscribeHost();
            unsubscribeHostRequests();
        };
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) return;
        // Listener for ALL notifications
        const notificationsRef = collection(db, 'notifications');
        const q = query(notificationsRef, where('userId', '==', currentUser.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const unreadCount = snapshot.docs.filter(doc => !doc.data().isRead).length;
            setNotificationCount(unreadCount);
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if (pendingCount > prevPendingCount.current) {
            // Dismiss any existing toasts
            // toast.custom((t) => (
            //     <div
            //         className={`custom-toast ${t.visible ? 'animate-enter' : 'animate-leave'}`}
            //         style={{
            //             background: 'white',
            //             padding: '12px 20px',
            //             borderRadius: '10px',
            //             boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
            //             display: 'flex',
            //             alignItems: 'center',
            //             gap: '12px'
            //         }}
            //     >
            //         <span>You have a new dinner request!</span>
            //         <button
            //             onClick={() => {
            //                 navigate('/profile?tab=pending&pendingTab=host');
            //                 toast.dismiss(t.id);
            //             }}
            //             style={{
            //                 background: '#1a6fa4',
            //                 color: 'white',
            //                 border: 'none',
            //                 borderRadius: '6px',
            //                 padding: '6px 12px',
            //                 cursor: 'pointer'
            //             }}
            //         >
            //             Review
            //         </button>
            //     </div>
            // ), { duration: 6000 });
        }
        prevPendingCount.current = pendingCount;
    }, [pendingCount, navigate]);

    const handleHostDinnerClick = () => {
        navigate('/HostDinnerIntro');
    };

    const handleLogoClick = () => {
        if (onLogoClick) {
            onLogoClick();
        } else {
            navigate('/home');
        }
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    const handleGlobeClick = () => {
        setShowLangTooltip(true);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    // סגירת פופאפ שפה בלחיצה מחוץ
    useEffect(() => {
        function handleLangClickOutside(event) {
            if (langPopupRef.current && !langPopupRef.current.contains(event.target)) {
                setShowLangTooltip(false);
            }
        }
        if (showLangTooltip) {
            document.addEventListener('mousedown', handleLangClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleLangClickOutside);
        };
    }, [showLangTooltip]);

    const handleBellClick = () => {
        setShowNotifications((prev) => !prev);
    };

    // זיהוי מובייל ריאקטיבי
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <header className="app-header">
            <div className="logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                <img src="/logo2.png" alt="Shabbat Abroad Logo" className="logo-image" />
                <span className="logo-text">Shabbat Abroad</span>
            </div>
            <nav className="header-nav">
                {/* הסרתי את כפתור הפילטרים במובייל */}
                <button
                    className="host-dinner-button"
                    onClick={handleHostDinnerClick}
                >
                    Host a dinner
                </button>
                <button className="icon-button globe-btn" style={{ position: 'relative' }} onClick={handleGlobeClick}>
                    <FontAwesomeIcon icon={faGlobe} />
                    {showLangTooltip && (
                        <div className="lang-popup" ref={langPopupRef}>
                            <div className="lang-popup-title">Choose language:</div>
                            <div className="lang-btn-row">
                                <button className="lang-btn" onClick={e => {e.stopPropagation(); setShowLangTooltip(false);}}>עברית</button>
                                <button className="lang-btn" onClick={e => {e.stopPropagation(); setShowLangTooltip(false);}}>English</button>
                            </div>
                        </div>
                    )}
                </button>
                <button className="icon-button bell-btn" onClick={handleBellClick} style={{ position: 'relative' }}>
                    <FontAwesomeIcon icon={faBell} />
                    {(!showNotifications && notificationCount > 0) && (
                        <span style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: 'red',
                            color: 'white',
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2
                        }}>
                            1
                        </span>
                    )}
                    {showNotifications && (
                        <NotificationList 
                            onClose={() => setShowNotifications(false)}
                            onNotificationRead={() => setNotificationCount(c => Math.max(0, c - 1))}
                        />
                    )}
                </button>
                <div className="user-menu-button" ref={menuRef}>
                    <FontAwesomeIcon
                        icon={faBars}
                        className="menu-icon"
                        onClick={() => setMenuOpen((open) => !open)}
                        style={{ cursor: 'pointer' }}
                    />
                    {profileImg ? (
                        <img
                            src={profileImg || '/blankprofile.png'}
                            alt="Profile"
                            className="user-image"
                            onClick={handleProfileClick}
                            style={{
                                cursor: 'pointer',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                marginLeft: 8
                            }}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faUser}
                            className="user-icon"
                            onClick={handleProfileClick}
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                    {menuOpen && (
                        <div className="header-dropdown-menu">
                            <div className="dropdown-item" onClick={() => { setMenuOpen(false); navigate('/about'); }}>
                                About Us
                            </div>
                            <div
                                className="dropdown-item"
                                onClick={() => {
                                    setMenuOpen(false);
                                    navigate('/'); // מעבר לעמוד הראשי
                                }}
                            >
                                Log Out
                            </div>
                        </div>
                    )}
                </div>
            </nav>
        </header>
    );
}

export default Header;
